import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, FileText, Check, Download, Loader2 } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const DEFAULTS = {
    address: "",
    monthly_rent: 15000,
    split_type: "equal",
    security_deposit: 30000,
    utilities_split: "equal",
    quiet_hours_start: "22:00",
    quiet_hours_end: "07:00",
    cleaning_rotation: "weekly",
    guest_policy: "Overnight guests welcome with 1 day notice",
    pets_allowed: false,
    smoking_allowed: false,
    notice_period_days: 30,
    additional_clauses: "",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
};

export default function ContractModal({ open, matchId, otherName, onClose, currentUserName }) {
    const [step, setStep] = useState("list"); // list | form | view
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [signing, setSigning] = useState(false);
    const [form, setForm] = useState(DEFAULTS);
    const [selected, setSelected] = useState(null);

    const loadContracts = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/contracts/by-match/${matchId}`);
            setContracts(data.contracts || []);
        } catch (e) {} finally { setLoading(false); }
    };

    useEffect(() => {
        if (!open) {
            setStep("list");
            setSelected(null);
            setForm(DEFAULTS);
            return;
        }
        loadContracts();
    }, [open, matchId]);

    const create = async () => {
        setSaving(true);
        try {
            const payload = { ...form, match_id: matchId };
            if (form.split_type === "equal") {
                delete payload.my_share;
                delete payload.their_share;
            }
            if (!payload.end_date) delete payload.end_date;
            const { data } = await api.post("/contracts", payload);
            setContracts((cs) => [data, ...cs]);
            setSelected(data);
            setStep("view");
            toast.success("Contract created 📄");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't create contract");
        } finally {
            setSaving(false);
        }
    };

    const sign = async () => {
        if (!selected) return;
        setSigning(true);
        try {
            const { data } = await api.post(`/contracts/${selected.id}/sign`, {
                signed_name: currentUserName || "Anonymous",
            });
            setSelected(data);
            setContracts((cs) => cs.map((c) => (c.id === data.id ? data : c)));
            toast.success("Signed ✍️");
        } catch (e) {
            toast.error("Couldn't sign");
        } finally {
            setSigning(false);
        }
    };

    const openPdf = (contract) => {
        const token = localStorage.getItem("flatch_token") || "";
        const url = `${API_BASE}/contracts/${contract.id}/pdf${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
        // Use fetch to get the PDF with proper auth header, then blob URL
        fetch(`${API_BASE}/contracts/${contract.id}/pdf`, {
            headers: { Authorization: `Bearer ${token}` },
            credentials: "include",
        })
            .then((r) => r.blob())
            .then((blob) => {
                const objUrl = URL.createObjectURL(blob);
                window.open(objUrl, "_blank");
            })
            .catch(() => toast.error("Couldn't open PDF"));
    };

    if (!open) return null;

    const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
                data-testid="contract-modal"
                className="fixed inset-0 z-[85] bg-background/80 backdrop-blur-xl grid place-items-end sm:place-items-center p-3"
            >
                <motion.div
                    initial={{ y: "100%", opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: "100%", opacity: 0 }}
                    transition={{ type: "spring", stiffness: 260, damping: 30 }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full max-w-md rounded-4xl border-2 border-foreground bg-card p-5 pb-6 relative max-h-[92vh] flex flex-col"
                >
                    <div className="flex items-center justify-between mb-3 flex-shrink-0">
                        <div>
                            <div className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-1">
                                <FileText className="h-3 w-3" strokeWidth={3} /> agreement
                            </div>
                            <div className="font-display text-2xl font-black leading-tight">
                                Roommate contract
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            data-testid="contract-close-btn"
                            className="h-9 w-9 rounded-full hover:bg-accent grid place-items-center"
                        >
                            <X className="h-5 w-5" strokeWidth={2.5} />
                        </button>
                    </div>

                    <div className="overflow-y-auto flex-1 -mx-1 px-1">
                        {step === "list" && (
                            <ListView
                                contracts={contracts}
                                loading={loading}
                                onNew={() => setStep("form")}
                                onOpen={(c) => { setSelected(c); setStep("view"); }}
                                onOpenPdf={openPdf}
                            />
                        )}
                        {step === "form" && (
                            <FormView form={form} setF={setF} otherName={otherName} />
                        )}
                        {step === "view" && selected && (
                            <ViewContract
                                contract={selected}
                                otherName={otherName}
                                currentUserName={currentUserName}
                                onOpenPdf={() => openPdf(selected)}
                            />
                        )}
                    </div>

                    {/* Action buttons */}
                    {step === "form" && (
                        <div className="flex-shrink-0 flex gap-2 mt-3">
                            <Button
                                variant="outline"
                                onClick={() => setStep("list")}
                                data-testid="contract-cancel-btn"
                                className="h-12 rounded-2xl font-black"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={create}
                                disabled={saving || !form.address || !form.monthly_rent}
                                data-testid="contract-generate-btn"
                                className="flex-1 h-12 rounded-2xl font-black lift"
                            >
                                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                Generate contract
                            </Button>
                        </div>
                    )}
                    {step === "view" && selected && (
                        <div className="flex-shrink-0 flex gap-2 mt-3">
                            <Button
                                variant="outline"
                                onClick={() => setStep("list")}
                                data-testid="contract-back-btn"
                                className="h-12 rounded-2xl font-black"
                            >
                                Back
                            </Button>
                            <Button
                                onClick={sign}
                                disabled={signing || _iHaveSigned(selected)}
                                data-testid="contract-sign-btn"
                                className="flex-1 h-12 rounded-2xl font-black lift"
                            >
                                {_iHaveSigned(selected) ? (
                                    <><Check className="mr-2 h-4 w-4" /> Signed</>
                                ) : (
                                    <>Sign now</>
                                )}
                            </Button>
                        </div>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

function _iHaveSigned(contract) {
    // Best-effort: rely on backend `status` — we don't know which side we are here
    // but signing endpoint is idempotent per side; simplify: allow signing until both signed
    return contract?.status === "signed";
}

function ListView({ contracts, loading, onNew, onOpen, onOpenPdf }) {
    return (
        <div>
            <p className="text-xs text-muted-foreground mb-3">
                A fun-but-legit agreement covering rent split, quiet hours, chores, guests, and more.
                Both roommates sign digitally.
            </p>

            {loading ? (
                <div className="text-center text-sm text-muted-foreground py-6">Loading…</div>
            ) : contracts.length === 0 ? (
                <div className="rounded-3xl border-2 border-dashed border-border p-6 text-center">
                    <div className="text-3xl mb-2">📄</div>
                    <div className="font-display font-bold">No contracts yet</div>
                    <div className="text-xs text-muted-foreground mt-1">Create the first one below</div>
                </div>
            ) : (
                <div className="space-y-2" data-testid="contract-list">
                    {contracts.map((c) => (
                        <button
                            key={c.id}
                            onClick={() => onOpen(c)}
                            data-testid={`contract-row-${c.id}`}
                            className="w-full text-left flex items-center gap-3 rounded-2xl border border-border bg-background p-3 hover:bg-accent/40 lift"
                        >
                            <div className="h-10 w-10 rounded-xl bg-accent text-accent-foreground grid place-items-center">
                                <FileText className="h-5 w-5" strokeWidth={2.5} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold truncate">{c.address || "Untitled contract"}</div>
                                <div className="text-xs text-muted-foreground">
                                    ₹{(c.monthly_rent || 0).toLocaleString()}/mo · <span className={c.status === "signed" ? "text-primary font-bold" : ""}>{c.status.replace("_", " ")}</span>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            <button
                onClick={onNew}
                data-testid="contract-new-btn"
                className="mt-4 w-full h-12 rounded-2xl bg-primary text-primary-foreground font-black lift"
            >
                + New contract
            </button>
        </div>
    );
}

function FormView({ form, setF, otherName }) {
    return (
        <div className="space-y-3" data-testid="contract-form-view">
            <Field label="Flat address" testId="contract-field-address">
                <input
                    value={form.address}
                    onChange={(e) => setF("address", e.target.value)}
                    placeholder="e.g. 12A, Carter Rd, Bandra W, Mumbai"
                    className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm"
                    data-testid="contract-input-address"
                />
            </Field>

            <div className="grid grid-cols-2 gap-3">
                <Field label="Start date">
                    <input
                        type="date"
                        value={form.start_date}
                        onChange={(e) => setF("start_date", e.target.value)}
                        className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm"
                        data-testid="contract-input-start-date"
                    />
                </Field>
                <Field label="End date (optional)">
                    <input
                        type="date"
                        value={form.end_date}
                        onChange={(e) => setF("end_date", e.target.value)}
                        className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm"
                    />
                </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <Field label="Monthly rent (₹)">
                    <input
                        type="number"
                        value={form.monthly_rent}
                        onChange={(e) => setF("monthly_rent", parseInt(e.target.value) || 0)}
                        className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm"
                        data-testid="contract-input-rent"
                    />
                </Field>
                <Field label="Security deposit (₹)">
                    <input
                        type="number"
                        value={form.security_deposit}
                        onChange={(e) => setF("security_deposit", parseInt(e.target.value) || 0)}
                        className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm"
                    />
                </Field>
            </div>

            <Field label="Split type">
                <div className="flex gap-2">
                    {["equal", "custom"].map((s) => (
                        <button
                            key={s}
                            onClick={() => setF("split_type", s)}
                            className={`flex-1 h-10 rounded-xl border-2 text-xs font-black uppercase tracking-wider ${
                                form.split_type === s ? "border-primary bg-primary text-primary-foreground" : "border-border"
                            }`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </Field>

            <div className="grid grid-cols-2 gap-3">
                <Field label="Quiet hours start">
                    <input
                        type="time"
                        value={form.quiet_hours_start}
                        onChange={(e) => setF("quiet_hours_start", e.target.value)}
                        className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm"
                    />
                </Field>
                <Field label="Quiet hours end">
                    <input
                        type="time"
                        value={form.quiet_hours_end}
                        onChange={(e) => setF("quiet_hours_end", e.target.value)}
                        className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm"
                    />
                </Field>
            </div>

            <Field label="Cleaning rotation">
                <div className="flex gap-2">
                    {["weekly", "monthly", "on_demand"].map((v) => (
                        <button
                            key={v}
                            onClick={() => setF("cleaning_rotation", v)}
                            className={`flex-1 h-10 rounded-xl border-2 text-[10px] font-black uppercase tracking-wider ${
                                form.cleaning_rotation === v ? "border-primary bg-primary text-primary-foreground" : "border-border"
                            }`}
                        >
                            {v.replace("_", " ")}
                        </button>
                    ))}
                </div>
            </Field>

            <div className="grid grid-cols-2 gap-3">
                <ToggleField label="Pets allowed" value={form.pets_allowed} onChange={(v) => setF("pets_allowed", v)} />
                <ToggleField label="Smoking indoors" value={form.smoking_allowed} onChange={(v) => setF("smoking_allowed", v)} />
            </div>

            <Field label="Guest policy">
                <input
                    value={form.guest_policy}
                    onChange={(e) => setF("guest_policy", e.target.value)}
                    className="h-11 w-full rounded-xl border border-border bg-background px-3 text-sm"
                />
            </Field>

            <Field label="Additional clauses (optional)">
                <textarea
                    value={form.additional_clauses}
                    onChange={(e) => setF("additional_clauses", e.target.value)}
                    rows={3}
                    placeholder="e.g. Both parties agree to a monthly sit-down chat over chai to review anything that's not working."
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                />
            </Field>
        </div>
    );
}

function ViewContract({ contract, otherName, currentUserName, onOpenPdf }) {
    const both = contract.status === "signed";
    return (
        <div className="space-y-3" data-testid="contract-view-detail">
            <div className="rounded-2xl border-2 border-foreground bg-card p-4">
                <div className="text-[10px] font-black uppercase tracking-widest text-primary">
                    {both ? "fully signed ✍️" : contract.status.replace("_", " ")}
                </div>
                <div className="font-display font-black text-lg mt-1">{contract.address}</div>
                <div className="text-sm text-muted-foreground mt-1">
                    ₹{contract.monthly_rent?.toLocaleString()}/mo · Start {contract.start_date}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
                <SignSlot label="Party A" sig={contract.signature_a} />
                <SignSlot label="Party B" sig={contract.signature_b} />
            </div>

            <ul className="space-y-1 text-xs text-muted-foreground">
                <li>· Quiet hours {contract.quiet_hours_start}–{contract.quiet_hours_end}</li>
                <li>· Cleaning {contract.cleaning_rotation.replace("_", " ")}</li>
                <li>· Guests: {contract.guest_policy}</li>
                <li>· Pets {contract.pets_allowed ? "allowed" : "not allowed"} · Smoking {contract.smoking_allowed ? "allowed" : "not allowed inside"}</li>
                <li>· {contract.notice_period_days} days move-out notice</li>
            </ul>

            <button
                onClick={onOpenPdf}
                data-testid="contract-pdf-btn"
                className="w-full h-12 rounded-2xl border-2 border-foreground bg-card font-black flex items-center justify-center gap-2 lift"
            >
                <Download className="h-4 w-4" /> Open PDF
            </button>
        </div>
    );
}

function SignSlot({ label, sig }) {
    return (
        <div className={`rounded-2xl border-2 p-3 ${sig ? "border-primary bg-primary/5" : "border-dashed border-border"}`}>
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className="font-bold mt-0.5">
                {sig ? sig.signed_name : "— not signed —"}
            </div>
            {sig && <div className="text-[10px] text-muted-foreground mt-0.5">{sig.signed_at?.slice(0, 10)}</div>}
        </div>
    );
}

function Field({ label, children, testId }) {
    return (
        <div data-testid={testId}>
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
            {children}
        </div>
    );
}

function ToggleField({ label, value, onChange }) {
    return (
        <button
            onClick={() => onChange(!value)}
            className={`flex-1 h-10 rounded-xl border-2 text-xs font-black uppercase tracking-wider ${
                value ? "border-primary bg-primary text-primary-foreground" : "border-border"
            }`}
        >
            {label}: {value ? "yes" : "no"}
        </button>
    );
}
